import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./SenderNameLengthComplianceChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import { SENDERS_FILTER_KEY } from "../sendersDetailsShared";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardLegendTop,
    buildMonitorDashboardResponsiveChartOptions,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
    monitorDashboardLegendClickHandler,
} from "../../../chart/monitorDashboardChartKit";

@WithRender
@Component({
    diProvide: [RequestsSendersService],
})
export default class SenderNameLengthComplianceChart extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    @Prop({ required: true }) public filter!: any;

    private chart: any = null;
    private canvasId: string = "sender-name-length-compliance-chart-canvas";

    public hasData: boolean = true;
    public isLoading: boolean = false;
    public compliancePercentage: number = 0;
    private requestVersion: number = 0;
    private barLengths: number[] = [];
    private barCounts: number[] = [];

    public beforeDestroy(): void {
        this.destroyChart();
    }

    public onTitleClick(): void {
        this.navigateToDetails(null);
    }

    private navigateToDetails(nameLength: number | null): void {
        const f = this.filter;
        const sendersPayload = {
            dateFrom: f.dateFrom,
            dateTo: f.dateTo,
            tenantId: f.tenant,
            providerId: f.providerId,
            customerType: f.customerType || 0,
        };

        try {
            sessionStorage.setItem(
                SENDERS_FILTER_KEY,
                JSON.stringify(sendersPayload),
            );
        } catch (e) {
        }

        const query: any = {};
        if (nameLength != null && nameLength >= 2 && nameLength <= 11) {
            query.nameLength = String(nameLength);
        }

        this.$router.push({
            name: "SenderNameLengthComplianceDetails",
            query: query,
        });
    }

    private onChartClick(evt: any, elements: any[]): void {
        if (!elements || !elements.length) {
            return;
        }

        const index = elements[0]._index != null ? Number(elements[0]._index) : -1;
        if (index < 0 || index >= this.barLengths.length) {
            return;
        }

        const count = this.barCounts.length > index ? Number(this.barCounts[index]) : 0;
        if (!count || count <= 0) {
            return;
        }

        const nameLength = this.barLengths[index] != null ? Number(this.barLengths[index]) : 0;
        if (nameLength < 2 || nameLength > 11) {
            return;
        }

        this.navigateToDetails(nameLength);
    }

    public get compliancePercentageText(): string {
        return `${this.compliancePercentage.toFixed(2)}%`;
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;
        const resp = await this.requestsSendersService.GetSenderNameLengthCompliance(this.filter);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }
        this.isLoading = false;
        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.compliancePercentage = 0;
            this.barLengths = [];
            this.barCounts = [];
            this.destroyChart();
            return;
        }

        const rows = resp && resp.result && Array.isArray(resp.result) ? resp.result : [];

        const countsByKey: any = {};
        let totalCount = 0;
        let compliantCount = 0;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (!row) continue;
            const length = row.nameLength != null ? Number(row.nameLength) : 0;
            const customerType = row.customerType != null ? Number(row.customerType) : 0;
            const count = row.count != null ? Number(row.count) : 0;
            const isCompliant = row.isCompliant === true;

            if (length < 2 || length > 11 || count < 0) continue;
            countsByKey[`${length}:${customerType}`] = count;
            totalCount += count;
            if (isCompliant) {
                compliantCount += count;
            }
        }

        this.compliancePercentage = totalCount === 0
            ? 0
            : (compliantCount * 100) / totalCount;

        const lengths: number[] = [];
        for (let i = 2; i <= 11; i++) {
            lengths.push(i);
        }

        const governmentValues: number[] = [];
        const privateValues: number[] = [];
        const charityValues: number[] = [];
        const barCounts: number[] = [];
        for (let i = 0; i < lengths.length; i++) {
            const length = lengths[i];
            const governmentCount = this.getCount(countsByKey, length, 1);
            const privateCount = this.getCount(countsByKey, length, 3);
            const charityCount = this.getCount(countsByKey, length, 2);
            governmentValues.push(governmentCount);
            privateValues.push(privateCount);
            charityValues.push(charityCount);
            barCounts.push(governmentCount + privateCount + charityCount);
        }

        this.barLengths = lengths;
        this.barCounts = barCounts;
        this.hasData = totalCount > 0;
        await this.$nextTick();
        this.renderChart(lengths, governmentValues, privateValues, charityValues);
    }

    private getCount(countsByKey: any, length: number, customerType: number): number {
        const key = `${length}:${customerType}`;
        return countsByKey.hasOwnProperty(key) ? Number(countsByKey[key]) : 0;
    }

    private renderChart(lengths: number[], governmentValues: number[], privateValues: number[], charityValues: number[]): void {
        this.destroyChart();

        if (!this.hasData) return;

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;
        const fillGov = getMonitorDashboardChartColor("senderNameLengthCompliance", "1");
        const fillPrivate = getMonitorDashboardChartColor("senderNameLengthCompliance", "3");
        const fillCharity = getMonitorDashboardChartColor("senderNameLengthCompliance", "2");
        const xMax = this.computeNiceXAxisMax(governmentValues, privateValues, charityValues);
        const fontFamily = getMonitorDashboardFontFamily();
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();

        const self = this;

        this.chart = new Chart(ctx, {
            type: "horizontalBar",
            data: {
                labels: lengths.map(function (x) { return `${x} Characters`; }),
                datasets: [
                    {
                        label: this.$t("monitorDashboard.government").toString(),
                        data: governmentValues,
                        backgroundColor: fillGov,
                        borderColor: monitorDashboardDarkerBorder(fillGov),
                        borderWidth: 2,
                    },
                    {
                        label: this.$t("monitorDashboard.private").toString(),
                        data: privateValues,
                        backgroundColor: fillPrivate,
                        borderColor: monitorDashboardDarkerBorder(fillPrivate),
                        borderWidth: 2,
                    },
                    {
                        label: this.$t("monitorDashboard.charity").toString(),
                        data: charityValues,
                        backgroundColor: fillCharity,
                        borderColor: monitorDashboardDarkerBorder(fillCharity),
                        borderWidth: 2,
                    },
                ],
            },
            plugins: [
                {
                    afterDatasetsDraw: function (chart: any) {
                        self.drawValuesInsideBars(chart);
                    },
                },
            ],
            options: Object.assign({}, baseOpts, {
                legend: buildMonitorDashboardLegendTop(fontFamily, monitorDashboardLegendClickHandler),
                onClick: function (evt: any, elements: any[]) {
                    self.onChartClick(evt, elements);
                },
                scales: {
                    xAxes: [{
                        stacked: true,
                        ticks: {
                            beginAtZero: true,
                            precision: 0,
                            max: xMax,
                            fontColor: "#6b7280",
                            fontFamily: fontFamily,
                        },
                    }],
                    yAxes: [{
                        stacked: true,
                        gridLines: { display: false },
                        ticks: {
                            fontColor: "#6b7280",
                            fontFamily: fontFamily,
                        },
                    }],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    enabled: true,
                    mode: "index",
                    intersect: false,
                    callbacks: {
                        title: function (items: any) {
                            if (!items || !items.length) return "";
                            return String(items[0].yLabel || "");
                        },
                        label: function (item: any, data: any) {
                            const dsIndex = item.datasetIndex;
                            const idx = item.index;
                            const ds = data && data.datasets && data.datasets[dsIndex] ? data.datasets[dsIndex] : null;
                            const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
                            const value = isNaN(raw) ? 0 : raw;
                            return String(ds && ds.label ? ds.label : "") + ": " + String(value);
                        },
                        afterBody: function (items: any[], data: any) {
                            let total = 0;
                            if (items && items.length) {
                                for (let i = 0; i < items.length; i++) {
                                    const item = items[i];
                                    const ds = data && data.datasets && data.datasets[item.datasetIndex]
                                        ? data.datasets[item.datasetIndex]
                                        : null;
                                    const raw = ds && ds.data && ds.data.length > item.index ? Number(ds.data[item.index]) : 0;
                                    const value = isNaN(raw) ? 0 : raw;
                                    total += value;
                                }
                            }
                            return String(self.$t("monitorDashboard.total")) + ": " + String(total);
                        },
                    },
                }),
            }),
        });
    }

    private drawValuesInsideBars(chart: any): void {
        const ctx: any = chart.ctx;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "12px " + getMonitorDashboardFontFamily();
        ctx.fillStyle = "#FFFFFF";

        const datasets = chart.data && chart.data.datasets ? chart.data.datasets : [];
        for (let di = 0; di < datasets.length; di++) {
            const dataset = datasets[di];
            const meta = chart.getDatasetMeta(di);
            if (!meta || meta.hidden || !meta.data) continue;

            for (let i = 0; i < meta.data.length; i++) {
                const value = Number(dataset.data[i] || 0);
                if (value <= 0) continue;

                const bar = meta.data[i];
                const model = bar && bar._model ? bar._model : null;
                if (!model) continue;

                const barWidth = Math.abs(Number(model.base) - Number(model.x));
                if (barWidth < 18) continue;

                const leftX = Math.min(Number(model.base), Number(model.x));
                const centerX = leftX + (barWidth / 2);
                ctx.fillText(String(value), centerX, model.y);
            }
        }

        ctx.restore();
    }

    private computeNiceXAxisMax(governmentValues: number[], privateValues: number[], charityValues: number[]): number {
        let maxVal = 0;
        for (let i = 0; i < governmentValues.length; i++) {
            const total = Number(governmentValues[i]) + Number(privateValues[i]) + Number(charityValues[i]);
            if (total > maxVal) maxVal = total;
        }

        if (maxVal <= 0) return 10;
        if (maxVal <= 20) return 20;
        if (maxVal <= 50) return Math.ceil(maxVal / 5) * 5;
        if (maxVal <= 120) return Math.ceil(maxVal / 10) * 10;
        if (maxVal <= 300) return Math.ceil(maxVal / 20) * 20;

        return Math.ceil(maxVal / 50) * 50;
    }

}
